import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Code2, Search, FileCheck2, Cpu, Rocket, Loader2, ArrowRight, MonitorSmartphone, Store, Sparkles, Plus, Video, Presentation, HeartPulse, LineChart, GraduationCap, ShieldCheck, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { DEV_GROUPS } from "../lib/domains";
import { ProductCard } from "../components/ProductCard";
import { Reveal, EASE } from "../components/Motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = [
  { icon: Search, title: "Analyse de votre besoin", desc: "Écoute et cadrage de votre projet : objectifs, utilisateurs, contraintes métier." },
  { icon: FileCheck2, title: "Cahier des charges avec IA", desc: "Rédaction structurée et validation du cahier des charges, assistée par IA." },
  { icon: Cpu, title: "Développement par IA en quelques semaines", desc: "Réalisation complète par Intelligence Artificielle Générative, supervisée par nos experts." },
  { icon: Rocket, title: "Livraison, formation et support", desc: "Mise en production, formation de vos équipes et accompagnement continu." },
];

const EMPTY_FORM = { contact_name: "", establishment: "", country: "", email: "", phone: "", project_type: "", message: "" };

export default function DevInformatique() {
  const [products, setProducts] = useState([]);
  const [pvcId, setPvcId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setProducts(data.filter((p) => p.category === "Développement informatique"));
      const pvc = data.find((p) => p.title.includes("Patient Vitalis Care"));
      if (pvc) setPvcId(pvc.product_id);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/quotes", {
        contact_name: form.contact_name, establishment: form.establishment,
        country: form.country, email: form.email, phone: form.phone,
        project_type: form.project_type, message: form.message, qty: 1,
      });
      toast.success("Demande envoyée ! Le Cabinet CARIS Santé vous recontacte rapidement.");
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(apiError(err, "Envoi impossible"));
    } finally {
      setBusy(false);
    }
  };

  const realisations = [
    {
      icon: MonitorSmartphone, title: "Patient Vitalis Care",
      desc: "Système d'information hospitalier complet : dossiers patients, facturation CMU, statistiques.",
      to: pvcId ? `/produit/${pvcId}` : "/catalogue", cta: "Voir la présentation", testid: "realisation-pvc",
    },
    {
      icon: Store, title: "Vitalis Digital Store",
      desc: "Boutique en ligne de produits digitaux médicaux : paiements Mobile Money et carte bancaire.",
      to: "/catalogue", cta: "Visiter la boutique", testid: "realisation-vds",
    },
    {
      icon: Video, title: "Plateforme de téléconsultation", demo: true,
      impact: "Objectif type : accès aux soins à distance, −40 % de déplacements patients",
      desc: "Cas de démonstration : téléconsultations et téléexpertises avec visio sécurisée, comptes-rendus PDF et paiement.",
      to: "/produit/prod_290433f02116", cta: "Voir la démonstration", testid: "realisation-demo-tele",
    },
    {
      icon: Presentation, title: "Plateforme de webinaires & staffs", demo: true,
      impact: "Objectif type : staffs et formations à distance, présence tracée automatiquement",
      desc: "Cas de démonstration : gestion des réunions, séminaires et webinaires en ligne avec inscriptions et enregistrement.",
      to: "/produit/prod_121423797aa2", cta: "Voir la démonstration", testid: "realisation-demo-webinaire",
    },
    {
      icon: LineChart, title: "Plateforme de Suivi-Évaluation",
      desc: "Gestion des activités, finances, ressources humaines et autres, destinée aux managers d'entreprises et gestionnaires d'hôpitaux.",
      to: "/catalogue", cta: "En savoir plus", testid: "realisation-suivi-evaluation",
    },
    {
      icon: GraduationCap, title: "Plateforme E-learning",
      desc: "Cours en ligne par visioconférence ou téléchargement de documents, pour la formation à distance.",
      to: "/catalogue", cta: "En savoir plus", testid: "realisation-elearning",
    },
    {
      icon: ShieldCheck, title: "Plateforme de Messagerie Internet Sécurisée",
      desc: "Solution de communication sécurisée pour les professionnels.",
      to: "/catalogue", cta: "En savoir plus", testid: "realisation-messagerie-securisee",
    },
    {
      icon: Stethoscope, title: "Plateforme de Téléconsultation médicale",
      desc: "Consultation médicale à distance entre médecins et patients depuis leurs domiciles.",
      to: "/catalogue", cta: "En savoir plus", testid: "realisation-teleconsultation-medicale",
    },
  ];

  return (
    <div data-testid="dev-informatique-page">
      {/* HERO */}
      <section className="bg-navy py-20 text-white">
        <div className="mx-auto max-w-7xl px-6">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="mb-4 inline-flex items-center gap-2 rounded-full border border-sky-400/40 bg-sky-400/10 px-4 py-1.5 font-mono text-xs uppercase tracking-widest text-sky-300">
            <Code2 size={13} /> Cabinet CARIS Santé
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
            className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
            Développement Informatique
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7, ease: EASE }}
            className="mt-5 max-w-3xl text-base leading-relaxed text-slate-300 md:text-lg" data-testid="dev-intro">
            Cabinet CARIS Santé conçoit des applications médicales, des systèmes d'information hospitaliers et des
            solutions numériques sur mesure, entièrement développés par Intelligence Artificielle Générative.
            Résultat garanti en quelques semaines, à une fraction du coût traditionnel.
          </motion.p>
        </div>
      </section>

      {/* RÉALISATIONS */}
      <section className="mx-auto max-w-7xl px-6 py-20">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-widest text-ciel">Portfolio</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Nos réalisations</h2>
          <p className="mt-3 max-w-2xl text-sm text-slate-500" data-testid="realisations-note">
            Projets internes du Cabinet CARIS Santé et cas de démonstration illustratifs. Vos références clients viendront enrichir ce portfolio.
          </p>
        </Reveal>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {realisations.map((r, i) => (
            <Reveal key={r.title} delay={i * 0.08}>
              <Link to={r.to} data-testid={r.testid}
                className="group flex h-full flex-col rounded-xl border border-slate-200 bg-white p-6 transition-shadow duration-300 hover:shadow-lg">
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-ciel"><r.icon size={22} /></span>
                  {r.demo && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700" data-testid="realisation-demo-badge">Démonstration</span>}
                </div>
                <h3 className="mt-5 font-display text-base font-semibold text-navy">{r.title}</h3>
                {r.impact && <p className="mt-2 inline-block rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700" data-testid="realisation-impact">{r.impact}</p>}
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500">{r.desc}</p>
                <span className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-ciel transition-colors duration-200 group-hover:text-navy">
                  {r.cta} <ArrowRight size={13} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROCESSUS */}
      <section className="border-y border-slate-200 bg-white py-20">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-widest text-ciel">Méthode</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Processus en 4 étapes</h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="relative h-full rounded-xl border border-slate-200 bg-slate-50/60 p-6" data-testid={`process-step-${i + 1}`}>
                  <span className="absolute right-5 top-5 font-display text-3xl font-bold text-slate-200">{i + 1}</span>
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy text-sky-400"><s.icon size={22} /></span>
                  <h3 className="mt-5 font-display text-sm font-semibold leading-snug text-navy">{s.title}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-slate-500">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PRESTATIONS DEV */}
      {products.length > 0 && (
        <section className="mx-auto max-w-7xl px-6 py-20">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-ciel">Catalogue</p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Nos prestations de développement</h2>
            </div>
            <Link to="/catalogue?cat=D%C3%A9veloppement%20informatique" data-testid="dev-catalogue-link"
              className="flex items-center gap-2 text-sm font-semibold text-ciel transition-colors duration-200 hover:text-navy">
              Tout voir <ArrowRight size={15} />
            </Link>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {products.map((p) => <ProductCard key={p.product_id} product={p} />)}
          </div>
        </section>
      )}

      {/* CONTACT */}
      <section className="bg-navy py-20 text-white">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-2">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-widest text-sky-400">Décrivez votre projet</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight md:text-4xl">Envoyez votre demande</h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-300">
              Application mobile, site web, système d'information ou intégration de paiement :
              décrivez votre besoin et recevez une réponse personnalisée du Cabinet CARIS Santé.
            </p>
            <div className="mt-8 flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 p-5">
              <Sparkles size={20} className="shrink-0 text-sky-400" />
              <p className="text-xs leading-relaxed text-slate-300">
                Tous nos développements sont réalisés par Intelligence Artificielle Générative, supervisés par des experts —
                rapidité et coût maîtrisé garantis.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 text-navy shadow-xl" data-testid="dev-contact-form">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input data-testid="dev-contact-name" required placeholder="Nom et Prénoms *" value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
                <Input data-testid="dev-contact-institution" required placeholder="Institution *" value={form.establishment}
                  onChange={(e) => setForm({ ...form, establishment: e.target.value })} />
                <Input data-testid="dev-contact-country" placeholder="Pays" value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })} />
                <Input data-testid="dev-contact-email" type="email" required placeholder="Email *" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <Input data-testid="dev-contact-phone" required placeholder="Téléphone *" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <Select value={form.project_type} onValueChange={(v) => setForm({ ...form, project_type: v })}>
                  <SelectTrigger data-testid="dev-contact-type"><SelectValue placeholder="Type de projet souhaité" /></SelectTrigger>
                  <SelectContent>
                    {DEV_GROUPS.map((g) => (
                      <SelectGroup key={g.key}>
                        <SelectLabel className="text-xs uppercase tracking-wide text-slate-400">{g.label}</SelectLabel>
                        {g.types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <textarea data-testid="dev-contact-message" rows={4} required placeholder="Description du besoin *"
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-4 w-full rounded-md border border-slate-300 p-3 text-sm outline-none focus:ring-2 focus:ring-ciel" />
              <Button data-testid="dev-contact-submit" disabled={busy}
                className="mt-4 w-full rounded-full bg-ciel py-6 text-sm font-semibold text-white hover:bg-sky-700">
                {busy ? <Loader2 className="animate-spin" size={16} /> : "Envoyer ma demande"}
              </Button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
